import { useState, useEffect, useCallback, useRef } from "react";
import * as api from "../../api";
import {
  celebrationStorageKey, hasAllOrderItemsDelivered, isCelebrationEligibleOrder,
} from "../../components/game/rechargeCelebrationConfig";

/* PASO 5 (F3B) — Polling de órdenes (cliente 6s + admin 6s + catálogo 30s),
 * chimes, banners de nuevos pedidos/chats, cursor pagination y merge/dedup.
 * Código movido VERBATIM desde App.js — semántica intacta. */

/** Solo se avisa de recargas completadas hace menos de 15 min (o completadas
 *  mientras el cliente tiene la app abierta). Las antiguas se marcan como
 *  vistas en silencio para que no vuelvan a aparecer en cada inicio de sesión. */
const COMPLETION_NOTICE_WINDOW_MS = 15 * 60 * 1000;

const completedAtMs = (order) => {
  const ts = Date.parse(order?.completedAt || "");
  return Number.isNaN(ts) ? null : ts;
};

export function useOrderPolling({
  user, activeRole, orders, setOrders, setGames, setSelectedGame,
  playChimeSound, playChatChime, setUser,
}) {
  const [adminLoadMoreBusy, setAdminLoadMoreBusy] = useState(false);
  const [adminHasMoreOrders, setAdminHasMoreOrders] = useState(true);
  const [newOrderBanner, setNewOrderBanner] = useState(null);
  const [newChatBanner, setNewChatBanner] = useState(null);
  const [completedNotif, setCompletedNotif] = useState(null);
  const roleRef = useRef(null);
  const knownOrderIdsRef = useRef(null);
  const shownOrderStatusRef = useRef({});
  const lastStatusRef = useRef({});

  const acknowledgeCompletionOrders = useCallback(async (orderIds) => {
    const ids = [...new Set((orderIds || []).filter(Boolean))];
    if (!ids.length) return;
    setCompletedNotif((current) => {
      const remaining = (current?.orders || []).filter((order) => !ids.includes(order.id));
      return remaining.length ? { orders: remaining } : null;
    });
    try {
      await api.acknowledgeOrdersBatch(ids);
      setOrders((current) => current?.map(
        (order) => ids.includes(order.id) ? { ...order, acknowledgedByClient: true } : order,
      ));
    } catch (error) {
      console.warn("acknowledgeOrdersBatch failed:", error);
    }
  }, [setOrders]);

  const loadOrders = useCallback(async () => {
    try {
      let isAdmin = roleRef.current === "admin";
      if (!isAdmin) {
        try {
          const cached = JSON.parse(window.sessionStorage?.getItem("ag7_last_user") || "null");
          if (cached?.role === "admin") isAdmin = true;
        } catch { /* ignore */ }
      }
      const ord = await api.listOrders({ light: true, limit: isAdmin ? 15 : undefined });
      setOrders(ord);
      if (isAdmin) setAdminHasMoreOrders(ord.length >= 15);
    } catch (e) {
      if (e.status !== 401) console.error("Orders load failed", e);
    }
  }, [setOrders]);

  const loadMoreAdminOrders = useCallback(async () => {
    if (adminLoadMoreBusy || !adminHasMoreOrders) return;
    setAdminLoadMoreBusy(true);
    try {
      const oldest = orders.length ? orders[orders.length - 1] : null;
      const cursor = oldest?.createdAt;
      if (!cursor) { setAdminHasMoreOrders(false); return; }
      const next = await api.listOrders({
        light: true, limit: 15, beforeCreatedAt: cursor,
      });
      if (!next || next.length === 0) {
        setAdminHasMoreOrders(false);
        return;
      }
      const knownIds = new Set(orders?.map((o) => o.id));
      const additions = next?.filter((o) => !knownIds.has(o.id));
      setOrders((prev) => [...prev, ...additions]);
      setAdminHasMoreOrders(next.length >= 15);
    } catch (e) {
      console.warn("loadMoreAdminOrders failed:", e);
    } finally {
      setAdminLoadMoreBusy(false);
    }
  }, [orders, adminLoadMoreBusy, adminHasMoreOrders, setOrders]);

  // ===== POLL cliente: órdenes completadas + catálogo 30s =====
  useEffect(() => {
    // Cambio de cuenta en la misma pestaña: nada del cliente anterior se arrastra.
    shownOrderStatusRef.current = {};
    lastStatusRef.current = {};
    setCompletedNotif(null);
  }, [user?.id]);

  useEffect(() => {
    if (!user || activeRole !== "client") return;

    const checkForCompletions = async (currentOrders) => {
      const mine = (currentOrders || []).filter(
        (o) => !o.userId || o.userId === user.id
      );
      const candidates = mine.filter((o) =>
        o.status === "Completado" &&
        hasAllOrderItemsDelivered(o) &&
        !o.acknowledgedByClient &&
        shownOrderStatusRef.current[o.id] !== "shown"
      );
      // Una recarga se anuncia si se acaba de completar (ventana de 15 min) o si
      // cambió de estado a Completado con la app abierta.
      const justCompleted = [];
      const stale = [];
      for (const o of candidates) {
        const alreadyCelebrated = isCelebrationEligibleOrder(o)
          && window.localStorage?.getItem(celebrationStorageKey(o.id)) === "1";
        if (alreadyCelebrated) {
          stale.push(o);
          continue;
        }
        const previous = lastStatusRef.current[o.id];
        const ms = completedAtMs(o);
        const recent = ms !== null && Date.now() - ms <= COMPLETION_NOTICE_WINDOW_MS;
        if (recent || (previous && previous !== "Completado")) justCompleted.push(o);
        else stale.push(o);
      }
      for (const o of mine) lastStatusRef.current[o.id] = o.status;

      if (stale.length) {
        const ids = stale.map((o) => o.id);
        ids.forEach((id) => { shownOrderStatusRef.current[id] = "shown"; });
        api.acknowledgeOrdersBatch(ids)
          .then(() => setOrders((prev) => prev?.map(
            (o) => ids.includes(o.id) ? { ...o, acknowledgedByClient: true } : o
          )))
          .catch((e) => console.warn("silent acknowledge failed:", e));
      }

      if (justCompleted.length) {
        justCompleted.forEach((o) => { shownOrderStatusRef.current[o.id] = "shown"; });
        justCompleted.filter(isCelebrationEligibleOrder).forEach((o) => {
          try { window.localStorage?.setItem(celebrationStorageKey(o.id), "1"); }
          catch (error) { console.warn("celebration dedup write failed:", error); }
        });
        // Un solo aviso agrupado, aunque se completen varias recargas a la vez.
        setCompletedNotif((prev) => {
          const merged = [...(prev?.orders || []), ...justCompleted];
          const unique = [...new Map(merged.map((o) => [o.id, o])).values()];
          return { orders: unique };
        });
        playChimeSound();
        try {
          const refreshedUser = await api.me();
          setUser?.(refreshedUser);
          window.sessionStorage?.setItem("ag7_last_user", JSON.stringify(refreshedUser));
        } catch (e) {
          if (e.status !== 401) console.warn("wallet refresh after completion failed:", e);
        }
      }
    };

    void checkForCompletions(orders);

    const interval = setInterval(async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const fresh = await api.listOrders({ light: true });
        setOrders(fresh);
        await checkForCompletions(fresh);
      } catch (e) {
        if (e.status !== 401) console.error("Poll orders (client) failed:", e);
      }
    }, 6000);

    const onVis = async () => {
      if (typeof document === "undefined" || document.hidden) return;
      try {
        const fresh = await api.listOrders({ light: true });
        setOrders(fresh);
        await checkForCompletions(fresh);
      } catch (e) {
        if (e.status !== 401) console.warn("Client visibility refresh failed:", e);
      }
    };
    document.addEventListener("visibilitychange", onVis);

    const gamesInterval = setInterval(async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const fresh = await api.listGames({ light: true });
        setGames(fresh);
        try {
          const light = fresh?.map((g) => ({ ...g, image: "" }));
          window.localStorage?.setItem("ag7_games_cache", JSON.stringify(light));
        } catch (e) { console.warn("games cache poll write failed:", e); }
        setSelectedGame(prev => prev ? (fresh?.find(g => g.id === prev.id) || null) : prev);
      } catch (e) { console.warn("games catalog poll failed:", e); }
    }, 30000);
    return () => {
      clearInterval(interval);
      clearInterval(gamesInterval);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user, activeRole, playChimeSound, orders, setOrders, setGames, setSelectedGame, setUser]);

  // ===== POLL admin: nuevos pedidos + chat (con sonido) =====
  useEffect(() => {
    if (!user || activeRole !== "admin" || user.role !== "admin") return;

    let seeded = false;
    let lastUnread = new Map();

    const detectNew = (fresh) => {
      if (!seeded) {
        knownOrderIdsRef.current = new Set(fresh?.map(o => o.id));
        lastUnread = new Map(fresh?.map(o => [o.id, Number(o.unread_messages || 0)]));
        seeded = true;
        return;
      }
      const known = knownOrderIdsRef.current || new Set();
      const incoming = fresh?.filter(o => !known.has(o.id));
      if (incoming.length > 0) {
        incoming?.forEach(o => known.add(o.id));
        knownOrderIdsRef.current = known;
        playChimeSound();
        setNewOrderBanner({ count: incoming.length, ts: Date.now() });
        setTimeout(() => {
          setNewOrderBanner(nb => (nb && Date.now() - nb.ts >= 5900) ? null : nb);
        }, 6000);
      }
      let newChatCount = 0;
      const nextUnread = new Map();
      fresh?.forEach(o => {
        const now = Number(o.unread_messages || 0);
        const prev = lastUnread.get(o.id) || 0;
        if (now > prev) newChatCount += (now - prev);
        nextUnread.set(o.id, now);
      });
      lastUnread = nextUnread;
      if (newChatCount > 0) {
        playChatChime();
        setNewChatBanner({ count: newChatCount, ts: Date.now() });
        setTimeout(() => {
          setNewChatBanner(nb => (nb && Date.now() - nb.ts >= 5900) ? null : nb);
        }, 6000);
      }
    };

    const mergeTopWithExisting = (topRows) => {
      setOrders((prev) => {
        const topIds = new Set(topRows?.map((o) => o.id));
        const older = (prev || []).filter((o) => !topIds.has(o.id));
        return [...topRows, ...older];
      });
    };

    const interval = setInterval(async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const fresh = await api.listOrders({ light: true, limit: 15 });
        mergeTopWithExisting(fresh);
        detectNew(fresh);
      } catch (e) {
        if (e.status !== 401) console.error("Poll orders (admin) failed:", e);
      }
    }, 6000);

    const onVis = async () => {
      if (typeof document === "undefined" || document.hidden) return;
      try {
        const fresh = await api.listOrders({ light: true, limit: 15 });
        mergeTopWithExisting(fresh);
        detectNew(fresh);
      } catch (e) {
        if (e.status !== 401) console.warn("Admin visibility refresh failed:", e);
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user, activeRole, playChimeSound, playChatChime, setOrders]);

  useEffect(() => {
    if (activeRole !== "admin") {
      knownOrderIdsRef.current = null;
    }
  }, [activeRole]);

  useEffect(() => {
    roleRef.current = activeRole === "admin" ? "admin" : (user?.role || null);
  }, [user, activeRole]);

  const handleAcknowledgeCompletion = async () => {
    await acknowledgeCompletionOrders((completedNotif?.orders || []).map((order) => order.id));
  };

  return {
    loadOrders, loadMoreAdminOrders, adminLoadMoreBusy, adminHasMoreOrders,
    newOrderBanner, setNewOrderBanner, newChatBanner, setNewChatBanner,
    completedNotif, setCompletedNotif, handleAcknowledgeCompletion, acknowledgeCompletionOrders,
  };
}
